/**
 * Formatos de data, dinheiro e versão (§6.1).
 *
 * Existem aqui, e não soltos em cada schema, porque o valor de os três serem
 * iguais em toda a API é maior que a conveniência de cada endpoint escolher.
 */

import { z } from 'zod';

/**
 * Instante em ISO 8601 **UTC**, sempre com `Z`.
 *
 * ── Por que UTC e não o fuso do tenant ────────────────────────────────────
 *
 * A revenda opera em `America/Sao_Paulo`, e a tentação é devolver no fuso dela.
 * Mas o fuso é apresentação: quem decide como mostrar é a tela, que conhece o
 * `timezone` de `core.tenant_settings`. Serializar no fuso local faz o mesmo
 * instante ter duas representações — e no horário de verão, uma hora do ano
 * tem duas leituras válidas.
 */
export const InstanteSchema = z
  .string()
  .datetime({ offset: false })
  .describe('ISO 8601 em UTC, com sufixo Z. Ex.: 2026-08-07T19:04:45.000Z');

/** Só a data, sem hora — vencimento de tarefa, competência. */
export const DataSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .describe('ISO 8601, só a data. Ex.: 2026-08-07');

/**
 * Dinheiro em **unidade mínima**, com moeda explícita (§6.1).
 *
 * ── Por que inteiro e não decimal ─────────────────────────────────────────
 *
 * `number` em JSON é ponto flutuante binário, e 0,1 + 0,2 não dá 0,3 nele. Num
 * CRM de veículos os valores são altos e as somas frequentes — desconto,
 * entrada, financiamento —, e o erro aparece como centavo que não fecha, que é
 * o tipo de bug que ninguém consegue reproduzir.
 *
 * `1_599_00` centavos é exato, cabe em `Number.MAX_SAFE_INTEGER` com folga de
 * várias ordens de grandeza, e a conversão para exibição é do cliente.
 *
 * ── Por que a moeda é obrigatória ─────────────────────────────────────────
 *
 * Hoje é sempre BRL. "Sempre" durou até o primeiro cliente que importava carro.
 * O campo custa três letras agora e evita uma migração de dados depois.
 */
export const DinheiroSchema = z
  .object({
    valor: z
      .number()
      .int()
      .describe('Unidade mínima da moeda. Em BRL, centavos: 159900 = R$ 1.599,00'),
    moeda: z.string().length(3).describe('ISO 4217. Ex.: BRL'),
  })
  .describe('Valor monetário em unidade mínima, com moeda explícita (§6.1)');

export type Dinheiro = z.infer<typeof DinheiroSchema>;

/**
 * Versão de recurso conflitável, para o controle de concorrência da §6.1.
 *
 * Inteiro que a escrita incrementa. O cliente devolve em `If-Match`, e escrita
 * sobre versão antiga responde 409 — sem isso, dois vendedores movendo o mesmo
 * card produzem última-escrita-vence, e um deles perde a alteração sem saber.
 */
export const VersaoSchema = z
  .number()
  .int()
  .min(1)
  .describe('Versão do recurso. Devolva em If-Match ao atualizar.');
